/** `m_fLifeTime` (`WeaponTrace.cpp:453`), in seconds. */
export const WEAPON_TRAIL_LIFETIME_S = 0.18; // WeaponTrace.cpp:453
/** `m_fSamplingTime` (`WeaponTrace.cpp:455`), in seconds. */
export const WEAPON_TRACE_SAMPLING_S = 0.003; // WeaponTrace.cpp:455
/** `D3DXCOLOR(0.3f, 0.8f, 1.0f, a)` as bytes (`WeaponTrace.cpp:232`). */
const TRACE_RGB = `${Math.round(0.3 * 255)}, ${Math.round(0.8 * 255)}, ${Math.round(1.0 * 255)}`; // WeaponTrace.cpp:232

export interface WeaponTrailPoint {
  readonly xMm: number;
  readonly yMm: number;
  readonly zMm: number;
}

export interface WeaponTrailScreenPoint {
  readonly x: number;
  readonly y: number;
}

/** One sample of a trace list: how long ago, in seconds, and where. */
export interface WeaponTracePoint {
  ageS: number;
  readonly point: WeaponTrailPoint;
}

/** One rung of the ribbon: the tip and hilt the splines put at one age, and
 *  the tip edge's alpha. */
export interface WeaponTraceRung {
  readonly tip: WeaponTrailPoint;
  readonly hilt: WeaponTrailPoint;
  readonly alpha: number;
  readonly ageS: number;
}

/** One `CWeaponTrace`: the hilt list and the tip list, newest first. */
export class WeaponTrace {
  readonly hilts: WeaponTracePoint[] = [];
  readonly tips: WeaponTracePoint[] = [];
  private lastMs: number | null = null;

  /** `Update` (`WeaponTrace.cpp:27-121`): age both lists by the time since the
   *  last update and cut each after its first point past the lifetime, then,
   *  while the trace is on, push the blade's hilt and tip in front. A second
   *  update within one instant samples nothing. */
  update(nowMs: number, blade: { readonly hilt: WeaponTrailPoint; readonly tip: WeaponTrailPoint } | null): void {
    const elapsedS = this.lastMs === null ? 0 : Math.max(0, nowMs - this.lastMs) / 1000;
    if (this.lastMs !== null && elapsedS === 0) return;
    this.lastMs = nowMs;
    ageList(this.hilts, elapsedS);
    ageList(this.tips, elapsedS);
    if (blade === null) return;
    this.hilts.unshift({ ageS: 0, point: blade.hilt });
    this.tips.unshift({ ageS: 0, point: blade.tip });
  }
}

function ageList(list: WeaponTracePoint[], elapsedS: number): void {
  for (let i = 0; i < list.length; i += 1) {
    list[i].ageS += elapsedS;
    if (list[i].ageS > WEAPON_TRAIL_LIFETIME_S) { // WeaponTrace.cpp:39-44
      list.length = i + 1;
      return;
    }
  }
}

type V3 = [number, number, number];
const vec = (p: WeaponTrailPoint): V3 => [p.xMm, p.yMm, p.zMm];
const plus = (a: V3, b: V3): V3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const minus = (a: V3, b: V3): V3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const times = (a: V3, s: number): V3 => [a[0] * s, a[1] * s, a[2] * s];

/** `BuildVertex`'s spline for one list (`WeaponTrace.cpp:148-240`): the
 *  derivatives of the natural cubic through the first `n + 1` samples, solved
 *  as the original's tridiagonal sweep, sampled every `samplingS` from the
 *  newest sample back to the oldest's age or the lifetime. */
export function weaponTraceCurve(
  input: readonly WeaponTracePoint[],
  n: number,
  lifeS = WEAPON_TRAIL_LIFETIME_S,
  samplingS = WEAPON_TRACE_SAMPLING_S,
): { readonly ageS: number; readonly point: WeaponTrailPoint }[] {
  if (n < 1 || input.length <= n) return [];
  const p = (i: number): V3 => vec(input[i].point);
  const h: number[] = [];
  const r: V3[] = [];
  for (let i = 0; i < n; i += 1) {
    h[i] = input[i + 1].ageS - input[i].ageS;
    r[i] = times(minus(p(i + 1), p(i)), 3 / h[i]);
  }
  r[n] = [0, 0, 0];
  for (let i = n; i > 0; i -= 1) r[i] = plus(r[i], r[i - 1]);
  const stack: number[] = [];
  let rate = 0.5;
  r[0] = times(r[0], 0.5);
  stack.push(rate);
  for (let i = 1; i < n; i += 1) {
    r[i] = minus(r[i], r[i - 1]);
    rate = 1 / (4 - rate);
    r[i] = times(r[i], rate);
    stack.push(rate);
  }
  r[n] = minus(r[n], r[n - 1]);
  rate = 1 / (2 - rate);
  r[n] = times(r[n], rate);
  for (let i = n - 1; i >= 0; i -= 1) r[i] = minus(r[i], times(r[i + 1], stack.pop()!));

  const segment = (base: number): [V3, V3, V3, V3] => {
    const span = minus(p(base + 1), p(base));
    const hb = h[base];
    return [
      p(base),
      r[base],
      times(minus(minus(times(span, 3), times(r[base + 1], hb)), times(r[base], 2 * hb)), 1 / (hb * hb)),
      times(plus(times(span, -2), times(plus(r[base + 1], r[base]), hb)), 1 / (hb * hb * hb)),
    ];
  };
  const length = Math.min(lifeS, input[input.length - 1].ageS);
  let base = 0;
  let timebase = 0;
  let timenext = h[0];
  let [a, b, c, d] = segment(0);
  const out: { ageS: number; point: WeaponTrailPoint }[] = [];
  for (let t = 0; t <= length; t += samplingS) {
    while (t > timenext) {
      timebase = timenext;
      base += 1;
      if (base >= n) break;
      [a, b, c, d] = segment(base);
      timenext += h[base];
    }
    if (base > n) break;
    const cc = t - timebase;
    const at = plus(a, times(plus(b, times(plus(c, times(d, cc)), cc)), cc));
    out.push({ ageS: t, point: { xMm: at[0], yMm: at[1], zMm: at[2] } });
  }
  return out;
}

/** The ribbon `BuildVertex` hands `Render` (`WeaponTrace.cpp:123-278`): the tip
 *  curve and the hilt curve rung by rung, the tip edge at
 *  `(1 - t)^2 / 2.5 - 0.1` of the lifetime and the hilt edge clear. */
export function weaponTraceStrip(
  trace: WeaponTrace,
  lifeS = WEAPON_TRAIL_LIFETIME_S,
  samplingS = WEAPON_TRACE_SAMPLING_S,
): readonly WeaponTraceRung[] {
  if (trace.tips.length <= 1) return [];
  const n = trace.tips.length - 1;
  const tips = weaponTraceCurve(trace.tips, n, lifeS, samplingS);
  const hilts = weaponTraceCurve(trace.hilts, n, lifeS, samplingS);
  const firstAgeS = trace.tips[0].ageS;
  const rungs: WeaponTraceRung[] = [];
  for (let i = 0; i < Math.min(tips.length, hilts.length); i += 1) {
    const ageS = tips[i].ageS + firstAgeS;
    const ttt = Math.min(Math.max(ageS / lifeS, 0), 1); // WeaponTrace.cpp:230
    const fade = ((1 - ttt) * (1 - ttt)) / 2.5 - 0.1; // WeaponTrace.cpp:231
    rungs.push({ tip: tips[i].point, hilt: hilts[i].point, alpha: Math.min(Math.max(fade, 0), 1), ageS });
  }
  return rungs;
}

/** `m_fLength` (`SetWeaponInstance`, `WeaponTrace.cpp:376-395`): how far the
 *  weapon model's bounding box reaches from its root bone. */
export function weaponTraceLengthMm(
  meshes: readonly { readonly bboxMin: readonly number[]; readonly bboxMax: readonly number[] }[],
): number {
  if (meshes.length === 0) return 0;
  const min = [Infinity, Infinity, Infinity];
  const max = [-Infinity, -Infinity, -Infinity];
  for (const mesh of meshes) {
    for (let axis = 0; axis < 3; axis += 1) {
      min[axis] = Math.min(min[axis], mesh.bboxMin[axis]);
      max[axis] = Math.max(max[axis], mesh.bboxMax[axis]);
    }
  }
  return Math.sqrt(Math.max(min[0] ** 2 + min[1] ** 2 + min[2] ** 2, max[0] ** 2 + max[1] ** 2 + max[2] ** 2));
}

type WeaponTrailContext = Pick<
  CanvasRenderingContext2D,
  "save" | "restore" | "beginPath" | "moveTo" | "lineTo" | "closePath" | "fill" | "createLinearGradient"
> & {
  fillStyle: CanvasRenderingContext2D["fillStyle"];
};

/** `Render` (`WeaponTrace.cpp:280-330`): the strip between tip and hilt,
 *  alpha-blended, each quad shaded from its clear hilt edge to its tip edge. */
export function drawWeaponTrail(
  ctx: WeaponTrailContext,
  strip: readonly WeaponTraceRung[],
  project: (point: WeaponTrailPoint) => WeaponTrailScreenPoint | null,
): void {
  if (strip.length < 2) return;
  ctx.save();
  for (let i = 0; i + 1 < strip.length; i += 1) {
    const alpha = (strip[i].alpha + strip[i + 1].alpha) / 2;
    if (alpha <= 0) continue;
    const t0 = project(strip[i].tip);
    const t1 = project(strip[i + 1].tip);
    const h0 = project(strip[i].hilt);
    const h1 = project(strip[i + 1].hilt);
    if (t0 === null || t1 === null || h0 === null || h1 === null) continue;
    const gradient = ctx.createLinearGradient((h0.x + h1.x) / 2, (h0.y + h1.y) / 2, (t0.x + t1.x) / 2, (t0.y + t1.y) / 2);
    gradient.addColorStop(0, `rgba(${TRACE_RGB}, 0)`);
    gradient.addColorStop(1, `rgba(${TRACE_RGB}, ${alpha})`);
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.moveTo(t0.x, t0.y);
    ctx.lineTo(t1.x, t1.y);
    ctx.lineTo(h1.x, h1.y);
    ctx.lineTo(h0.x, h0.y);
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();
}
